/**
 * Practice programs ("Lög æfingarinnar", shared/program.ts): saving one (editor), and the practice
 * the quick menu offers ("Æfing dagsins", practice/model pickPractice) — read from the cached core
 * calendar (today − 7 … today + 60, kept fresh by offline/prefetchCore; offline too).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { EventDTO, SetProgramRequest } from '../../shared/dto/events';
import { practiceListId, type ProgramItem } from '../../shared/program';
import { coreEventsRange } from '../offline/prefetchCore';
import { hasProgram, pickPractice } from '../practice/model';
import { patchEventEverywhere, useEvents, useServerNow } from './calendar';
import { api } from './client';
import { collectionKey } from './library';

/** The core calendar is refreshed by prefetchCore; the quick menu only reads it. */
const GLANCE_STALE_MS = 10 * 60_000;

/** PUT /events/:id/program → the event everywhere it is cached; its practice list is fetched again. */
export function useSetProgram(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: ProgramItem[]) =>
      api<EventDTO>(`/events/${encodeURIComponent(eventId)}/program`, { method: 'PUT', body: { items } satisfies SetProgramRequest }),
    onSuccess: (ev) => {
      patchEventEverywhere(qc, eventId, (old) => ({ ...old, program: ev.program }));
      void qc.invalidateQueries({ queryKey: collectionKey(practiceListId(eventId)) });
    },
  });
}

/** The practice the quick menu offers now (null: none with songs ongoing, today or within two weeks). */
export function useCurrentPractice(): EventDTO | null {
  const now = useServerNow(60_000);
  const { from, to } = coreEventsRange(now);
  const q = useEvents(from, to, { staleTime: GLANCE_STALE_MS });
  return useMemo(() => pickPractice(q.data ?? [], now), [q.data, now]);
}
